import React from "react";
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useUploadProgress, UploadBatchProgress } from '@/hooks/useUploadProgress';
import { supabase } from '@/integrations/supabase/client';

interface UploadProgressContextType {
  activeBatchId: string | null;
  startTracking: (batchId: string) => void;
  stopTracking: () => void;
  progress: UploadBatchProgress | null;
  isSubscribed: boolean;
  showToast: boolean;
  setShowToast: (show: boolean) => void;
}

const UploadProgressContext = createContext<UploadProgressContextType | null>(null);

export function UploadProgressProvider({ children }: { children: ReactNode }) {
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const { progress, isSubscribed, reset } = useUploadProgress(activeBatchId);

  const startTracking = useCallback((batchId: string) => {
    setActiveBatchId(batchId);
    setShowToast(true);
  }, []);

  const stopTracking = useCallback(() => {
    setActiveBatchId(null);
    setShowToast(false);
    reset();
  }, [reset]);

  // Clean up on sign-out to prevent stale Realtime subscriptions
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setActiveBatchId(null);
        setShowToast(false);
        reset();
        supabase.removeAllChannels();
      }
    });
    return () => subscription.unsubscribe();
  }, [reset]);

  return (
    <UploadProgressContext.Provider
      value={{ activeBatchId, startTracking, stopTracking, progress, isSubscribed, showToast, setShowToast }}
    >
      {children}
    </UploadProgressContext.Provider>
  );
}

export function useUploadProgressContext() {
  const ctx = useContext(UploadProgressContext);
  if (!ctx) throw new Error('useUploadProgressContext must be used within UploadProgressProvider');
  return ctx;
}
